import { ToWords } from "to-words";
import DownloadButton from "../DownloadButton";
import { convertAmountToWords } from "../../utils/Formatter";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/VPInvoiceLayout.css";

const pickValue = (source, keys, fallback = "") => {
  if (!source) return fallback;

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return fallback;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatAmount = (value) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return "";
  return numericValue.toFixed(2);
};

const cleanDescription = (value) => {
  if (value === undefined || value === null) return "";
  return String(value).trim();
};

const isLikelyTruncated = (value) => cleanDescription(value).endsWith("...");

const pickBestDescription = (source, keys, fallback = "-") => {
  if (!source) return fallback;

  const candidates = keys
    .map((key) => cleanDescription(source[key]))
    .filter((value) => value !== "");

  if (candidates.length === 0) return fallback;

  const nonTruncatedCandidate = candidates.find((value) => !isLikelyTruncated(value));
  return nonTruncatedCandidate || candidates[0] || fallback;
};

export const VPInvoiceLayout = ({ headDetails = {}, companyData = {}, itemDetails = [] }) => {
  const toWords = new ToWords();

  let organisationData = {};
  try {
    organisationData = JSON.parse(localStorage.getItem("organisation") || "{}");
  } catch (error) {
    organisationData = {};
  }

  const voucherNo = pickValue(headDetails, ["voucher_no", "tran_no", "transaction_no"]);
  const voucherDate = formatDate(
    pickValue(headDetails, ["date", "voucher_date", "tran_date", "created_at"])
  );
  const paidTo = pickValue(headDetails, ["paid_to", "payee_name", "party_name", "vendor_name"]);
  const narration = pickValue(headDetails, ["narration", "remarks", "remark",]);
  const woPurpose = pickValue(headDetails, ["wo_no_purpose", "purpose", "wo_no"]);

  const totalAmountRaw = pickValue(headDetails, [
    "v_debit_amt",
    "debit_amt",
    "amount",
    "net_amt",
    "gross_amt",
  ]);

  const totalAmount = formatAmount(totalAmountRaw);

  const amountInWords =
    pickValue(headDetails, ["amount_in_words", "rupees_in_words"]) ||
    (totalAmount
      ? `${convertAmountToWords(totalAmount, toWords)} only`
      : "");

  const primaryDescription = pickBestDescription(headDetails, [
    "description",
    "purpose",
    "wo_no_purpose",
    "narration",
    "remarks",
    "goods_name",
    "project_name",
  ]);

  const primaryAmount =
    formatAmount(
      pickValue(headDetails, [
        "v_debit_amt",
        "debit_amt",
        "amount",
        "net_amt",
        "gross_amt",
        "v_closing_amt",
      ])
    ) || "";

  const mappedItemRows = (Array.isArray(itemDetails) ? itemDetails : [])
    .map((item, index) => ({
      slNo: index + 1,
      description: pickBestDescription(item, [
        "description",
        "purpose",
        "narration",
        "remarks",
        "item_name",
        "goods_name",
      ]),
      amount:
        formatAmount(
          pickValue(item, ["amount", "v_debit_amt", "debit_amt", "net_amt"])
        ) || "",
    }))
    .filter((row) => row.description !== "-" || row.amount !== "");

  const voucherRows =
    mappedItemRows.length > 0
      ? mappedItemRows
      : [
        {
          slNo: 1,
          description: primaryDescription,
          amount: primaryAmount || totalAmount,
        },
      ];

  const downloadRows = voucherRows
    .filter((row) => row.slNo !== "")
    .map((row) => ({
      sl_no: row.slNo,
      description: row.description,
      amount: row.amount,
    }));

  const invoiceTableData = {
    headings: [
      { display_name: "SL.NO", field_name: "sl_no", is_internal: false },
      {
        display_name: "DESCRIPTION",
        field_name: "description",
        is_internal: false,
      },
      { display_name: "AMOUNT", field_name: "amount", is_internal: false },
    ],
    rows: downloadRows,
  };

  return (
    <div className="vp-voucher-page">
      <div className="vp-voucher-card">
        <div className="vp-voucher-header">
          <div className="vp-voucher-logo">
            <img
              src={organisationData?.logo_url || ProfilePlaceholder}
              alt="Company Logo"
              className="vp-voucher-logo-image"
            />
          </div>
          <div className="vp-voucher-header-text">
            <h2>{organisationData?.name || companyData?.company_name || "Fine Punch Fab Pvt. Ltd."}</h2>
            <h3>CASH/CHEQUE VOUCHER</h3>
          </div>
        </div>

        <div className="vp-voucher-meta-row">
          <div className="vp-line-field">Paid To: <span>{paidTo}</span></div>
          <div className="vp-line-field">Voucher No: <span>{voucherNo}</span></div>
          <div className="vp-line-field">Date: <span>{voucherDate}</span></div>
        </div>

        <div className="vp-line-field vp-words-line">Rupees In Words: <span>{amountInWords}</span></div>

        <table className="vp-voucher-table">
          <colgroup>
            <col className="vp-col-slno" />
            <col className="vp-col-desc" />
            <col className="vp-col-amount" />
          </colgroup>
          <thead>
            <tr>
              <th>SL.NO</th>
              <th>DESCRIPTION</th>
              <th>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {voucherRows.map((row, index) => (
              <tr key={index}>
                <td>{row.slNo}</td>
                <td className="vp-voucher-description">{row.description}</td>
                <td className="vp-voucher-amount">{row.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="vp-line-field">NARRATION: <span>{narration}</span></div>

        <div className="vp-voucher-bottom-row">
          <div className="vp-line-field">CHECK NO: <span>{pickValue(headDetails, ["check_no", "cheque_no"])}</span></div>
          <div className="vp-line-field">DATE: <span>{formatDate(pickValue(headDetails, ["check_date", "cheque_date"]))}</span></div>
          <div className="vp-line-field">BANK: <span>{pickValue(headDetails, ["bank", "bank_name", "through_via"])}</span></div>
        </div>

        <div className="vp-signature-row">
          <div>Prepared</div>
          <div>Accounts</div>
          <div>Authorised Sign</div>
          <div>Receiver&apos;s Sign</div>
        </div>

        <p className="invoice-system-generated-note">
          This is a system-generated document and does not require a signature.
        </p>

        <div className="invoice-buttons vp-download-actions">
          <DownloadButton
            tableData={invoiceTableData}
            fileName="voucher-payment"
            downloadFormats={["excel", "pdf", "full-page-pdf"]}
            fullPageDownload={true}
            fullPageSelector=".vp-voucher-page"
          />
        </div>
      </div>
    </div>
  );
};
